'use client';

import { c as _c } from "react/compiler-runtime";
import type { MouseEvent } from 'react';
import { Skeleton } from '@/comps/primitives/skeleton';
import { CHART_DATALABELS_FONT_COLOR } from '@/domains/chart/components/create-chart/charts/analytics/chart.config';
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { barFillGradient } from '@/domains/pages/ai-spend/utils/bar-fill';
import { jetbrainsMonoChartFontFamily } from '@/theme/utils/font.utils';
import { cn } from '@/utils/shad';
import { useCallback, useMemo, useState } from 'react';
export type SpendBarDatum = {
  key: string;
  label: string;
  value: number;
  color: string;
};
export type SpendBarsTotal = {
  label: string;
  value: number;
};
type SpendHorizontalBarsProps = {
  bars: SpendBarDatum[];
  formatValue: (value: number) => string;
  emptyMessage: string;
  /** Footer row summing the bars, the way the AI Proficiency methodology closes with its score. */
  total?: SpendBarsTotal | null;
  className?: string;
};
const BAR_ROW_GRID = 'grid grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-3';

/** 12px label text at leading-tight, so the skeleton lines match a bar's name. */
const BAR_LABEL_HEIGHT_CLASS = 'h-[15px]';

/** Placeholder rows while a breakdown loads, keeping the bar list's rhythm. */
export function SpendHorizontalBarsSkeleton(t0) {
  const $ = _c(7);
  const {
    rows: t1,
    className
  } = t0;
  const rows = t1 === undefined ? 4 : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("px-5 py-4", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== rows) {
    t3 = <div className="flex flex-col gap-2">{Array.from({
        length: rows
      }, _temp)}</div>;
    $[2] = rows;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <div className={t2} data-testid="spend-horizontal-bars-skeleton">{t3}</div>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp(_, index) {
  return <div key={index} className="flex min-h-[35px] items-center"><Skeleton className={cn(BAR_LABEL_HEIGHT_CLASS, "w-50")} /></div>;
}
export function SpendHorizontalBars(t0) {
  const $ = _c(56);
  const {
    bars,
    formatValue,
    emptyMessage,
    total,
    className
  } = t0;
  const [hoveredKey, setHoveredKey] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 0,
      y: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [tooltipPos, setTooltipPos] = useState(t1);
  let T0;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== bars || $[2] !== className || $[3] !== emptyMessage || $[4] !== formatValue || $[5] !== hoveredKey || $[6] !== tooltipPos || $[7] !== total) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const hoveredBar = bars.find(bar => bar.key === hoveredKey) ?? null;
      let t11;
      if ($[18] !== bars) {
        const values = bars.map(_temp2).filter(_temp3);
        t11 = values.length > 0 ? Math.max(...values) : 0;
        $[18] = bars;
        $[19] = t11;
      } else {
        t11 = $[19];
      }
      const maxValue = t11;
      let t12;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = (event, bar_1) => {
          setHoveredKey(bar_1.key);
          setTooltipPos({
            x: event.clientX,
            y: event.clientY
          });
        };
        $[20] = t12;
      } else {
        t12 = $[20];
      }
      const showBar = t12;
      let t13;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = event_0 => {
          setTooltipPos({
            x: event_0.clientX,
            y: event_0.clientY
          });
        };
        $[21] = t13;
      } else {
        t13 = $[21];
      }
      const moveBar = t13;
      let t14;
      if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = () => {
          setHoveredKey(null);
        };
        $[22] = t14;
      } else {
        t14 = $[22];
      }
      const hideBar = t14;
      if (bars.length === 0) {
        let t15;
        if ($[23] !== className) {
          t15 = cn("flex items-center justify-center px-5 py-8 text-sm text-muted-foreground", className);
          $[23] = className;
          $[24] = t15;
        } else {
          t15 = $[24];
        }
        let t16;
        if ($[25] !== emptyMessage || $[26] !== t15) {
          t16 = <div className={t15}>{emptyMessage}</div>;
          $[25] = emptyMessage;
          $[26] = t15;
          $[27] = t16;
        } else {
          t16 = $[27];
        }
        t10 = t16;
        break bb0;
      }
      if ($[28] !== className) {
        t6 = cn("relative px-5 py-4", className);
        $[28] = className;
        $[29] = t6;
      } else {
        t6 = $[29];
      }
      t7 = "spend-horizontal-bars";
      let t15;
      if ($[30] !== bars || $[31] !== formatValue || $[32] !== hoveredKey || $[33] !== maxValue) {
        let t16;
        if ($[35] !== formatValue || $[36] !== hoveredKey || $[37] !== maxValue) {
          t16 = bar_2 => {
            const widthRatio = maxValue > 0 ? bar_2.value / maxValue : 0;
            const isDimmed = hoveredKey != null && hoveredKey !== bar_2.key;
            return <div key={bar_2.key} className={cn(BAR_ROW_GRID, "min-h-[35px] transition-opacity", isDimmed && "opacity-35")} onMouseEnter={event_1 => showBar(event_1, bar_2)} onMouseMove={moveBar}><span className="truncate text-[12px] leading-tight font-medium text-muted-foreground">{bar_2.label}</span><div className="h-[17px] min-w-0 rounded-[3px] bg-muted/40"><div className="h-full rounded-[3px]" style={{
                  width: `${Math.max(widthRatio * 100, 2)}%`,
                  background: barFillGradient(bar_2.color, "horizontal"),
                  border: `1px solid ${bar_2.color}`
                }} /></div><span className="shrink-0 text-[12px] font-medium tabular-nums" style={{
                color: CHART_DATALABELS_FONT_COLOR,
                fontFamily: jetbrainsMonoChartFontFamily
              }}>{formatValue(bar_2.value)}</span></div>;
          };
          $[35] = formatValue;
          $[36] = hoveredKey;
          $[37] = maxValue;
          $[38] = t16;
        } else {
          t16 = $[38];
        }
        t15 = bars.map(t16);
        $[30] = bars;
        $[31] = formatValue;
        $[32] = hoveredKey;
        $[33] = maxValue;
        $[34] = t15;
      } else {
        t15 = $[34];
      }
      if ($[39] !== t15) {
        t8 = <div className="flex flex-col gap-2" onMouseLeave={hideBar}>{t15}</div>;
        $[39] = t15;
        $[40] = t8;
      } else {
        t8 = $[40];
      }
      if ($[41] !== formatValue || $[42] !== total) {
        t9 = total ? <div className={cn(BAR_ROW_GRID, "mt-3 border-t border-border pt-3")} data-testid="spend-horizontal-bars-total"><span className="col-span-2 truncate text-[12px] leading-tight font-medium text-foreground">{total.label}</span><span className="shrink-0 text-[12px] font-semibold tabular-nums" style={{
            color: CHART_DATALABELS_FONT_COLOR,
            fontFamily: jetbrainsMonoChartFontFamily
          }}>{formatValue(total.value)}</span></div> : null;
        $[41] = formatValue;
        $[42] = total;
        $[43] = t9;
      } else {
        t9 = $[43];
      }
      T0 = FloatingTooltip;
      t2 = hoveredBar != null;
      t3 = tooltipPos.x;
      t4 = tooltipPos.y;
      t5 = hoveredBar ? <ChartTooltipPanel title={hoveredBar.label} rows={[{
        label: "Value",
        value: formatValue(hoveredBar.value)
      }]} /> : null;
    }
    $[1] = bars;
    $[2] = className;
    $[3] = emptyMessage;
    $[4] = formatValue;
    $[5] = hoveredKey;
    $[6] = tooltipPos;
    $[7] = total;
    $[8] = T0;
    $[9] = t10;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    T0 = $[8];
    t10 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
    t9 = $[17];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[44] !== T0 || $[45] !== t2 || $[46] !== t3 || $[47] !== t4 || $[48] !== t5) {
    t11 = <T0 open={t2} x={t3} y={t4}>{t5}</T0>;
    $[44] = T0;
    $[45] = t2;
    $[46] = t3;
    $[47] = t4;
    $[48] = t5;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== t11 || $[51] !== t6 || $[52] !== t7 || $[53] !== t8 || $[54] !== t9) {
    t12 = <div className={t6} data-testid={t7}>{t8}{t9}{t11}</div>;
    $[50] = t11;
    $[51] = t6;
    $[52] = t7;
    $[53] = t8;
    $[54] = t9;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  return t12;
}
function _temp3(value) {
  return Number.isFinite(value);
}
function _temp2(bar_0) {
  return bar_0.value;
}